import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SecurityGateEvidence } from "@/components/suite/SecurityGateEvidence";
import { EnterpriseCrmSection, EnterpriseValuePanel } from "@/components/EnterpriseCrm";
import { Loader2, Building2, ArrowLeft, Download, ShieldCheck, FileQuestion, Network, LifeBuoy, AlertTriangle } from "lucide-react";

const Card = ({ children, className = "", tid }) => (
  <div className={`rounded-3xl bg-white border border-slate-100 p-6 ${className}`} data-testid={tid}>{children}</div>
);
const Pct = ({ label, value, tid }) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid={tid}>
    <div className="text-[10px] font-bold uppercase text-slate-400">{label}</div>
    <div className="font-heading text-3xl font-extrabold text-navy mt-1">{value}</div>
  </div>
);

export default function EnterpriseReadiness() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [qa, setQa] = useState(null);
  const [showQa, setShowQa] = useState(false);

  useEffect(() => {
    if (user && !user.super_admin) { navigate("/admin"); return; }
    api.get("/compliance/readiness-center").then(({ data }) => setD(data)).catch(() => {});
  }, [user, navigate]);

  const loadQa = () => {
    if (qa) { setShowQa(!showQa); return; }
    api.get("/compliance/readiness-center/questionnaire").then(({ data }) => { setQa(data); setShowQa(true); }).catch(() => toast.error("Failed"));
  };
  const dl = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url); toast.success("Generated from live system data");
    } catch { toast.error("Generation failed"); }
  };

  if (!d) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="enterprise-readiness">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/compliance" className="text-white/50 text-xs flex items-center gap-1 hover:text-white" data-testid="er-back"><ArrowLeft className="w-3.5 h-3.5" /> Compliance Command Center</Link>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><Building2 className="w-6 h-6 text-cobalt" /> ENTERPRISE READINESS CENTER</h1>
              <p className="text-white/60 text-xs mt-1 max-w-3xl">Everything a 100-lawyer firm, hospital network or insurance carrier asks for during due diligence — generated live from the Compliance Command Center. This page never stores its own data.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => dl("/compliance/readiness-center/rfp", "crash_ready_rfp_response.md")} data-testid="er-rfp"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 text-white text-xs font-bold hover:bg-white/20"><Download className="w-3.5 h-3.5" /> RFP/RFI Response</button>
              <button onClick={() => dl("/compliance/readiness-center/board-report", "crash_ready_board_report.md")} data-testid="er-board"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-cobalt text-white text-xs font-bold"><Download className="w-3.5 h-3.5" /> One-Click Board Report</button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        <SecurityGateEvidence />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Pct label="SOC 2 Readiness" value={`${d.soc2_readiness_pct}%`} tid="er-soc2" />
          <Pct label="HIPAA Readiness" value={`${d.hipaa_readiness_pct}%`} tid="er-hipaa" />
          <Pct label="50-State Privacy" value={`${d.state_privacy_coverage_pct}%`} tid="er-state" />
          <Pct label="Cyber Insurance" value={`${d.cyber_insurance.readiness_pct}%`} tid="er-cyber" />
          <Pct label="Audit Readiness" value={`${d.audit_readiness_pct}%`} tid="er-audit" />
          <Pct label="Avg Maturity" value={`${d.average_maturity}/5`} tid="er-maturity" />
        </div>

        <Card tid="er-summary">
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cobalt" /> Executive summary</h3>
          <p className="text-sm text-slate-600 mt-2">{d.executive_summary}</p>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card tid="er-cyber-checklist">
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-cobalt" /> Cyber-insurance readiness checklist</h3>
            <div className="mt-3 space-y-1.5">
              {d.cyber_insurance.checklist.map((c, i) => (
                <div key={i} className="flex justify-between items-center rounded-xl bg-slate-50 px-3 py-2 text-xs">
                  <span className="text-slate-600">{c.item}</span>
                  <span className={`font-bold ${c.met ? "text-emerald-600" : "text-amber-500"}`}>{c.met ? "✓ Met" : "In progress"}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card tid="er-dr">
            <h3 className="font-heading font-bold text-navy text-sm">Disaster recovery &amp; business continuity</h3>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-xl bg-slate-50 p-3"><div className="text-[10px] uppercase font-bold text-slate-400">DR control</div><div className="font-semibold text-navy mt-1">{d.disaster_recovery.control_status}</div></div>
              <div className="rounded-xl bg-slate-50 p-3"><div className="text-[10px] uppercase font-bold text-slate-400">Uptime (recent checks)</div><div className="font-semibold text-navy mt-1">{d.disaster_recovery.uptime_pct_recent_checks ?? "—"}%</div></div>
              <div className="rounded-xl bg-slate-50 p-3"><div className="text-[10px] uppercase font-bold text-slate-400">Monitored services</div><div className="font-semibold text-navy mt-1">{d.disaster_recovery.monitored_services}</div></div>
              <div className="rounded-xl bg-slate-50 p-3"><div className="text-[10px] uppercase font-bold text-slate-400">Last DR test</div><div className="font-semibold text-navy mt-1">{d.disaster_recovery.last_tested?.slice(0, 10) || "Not yet tested (honest)"}</div></div>
            </div>
            <h4 className="font-heading font-semibold text-navy text-xs mt-3">Continuity documentation</h4>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {d.business_continuity_docs.map((p) => <span key={p.name} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold">{p.name} · {p.version}</span>)}
            </div>
          </Card>
        </div>

        <Card tid="er-risk-register">
          <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Risk register ({d.risk_register.length} open) — overall score {d.overall_risk_score}/100 ({d.risk_band})</h3>
          <div className="mt-3 max-h-64 overflow-y-auto space-y-1.5">
            {d.risk_register.map((r, i) => (
              <div key={i} className="flex justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs">
                <span className="text-slate-600">{r.description} <span className="text-slate-300">· {r.category} · {r.treatment}</span></span>
                <span className={`font-bold whitespace-nowrap ${r.residual_risk === "high" ? "text-red-500" : r.residual_risk === "medium" ? "text-amber-500" : "text-emerald-600"}`}>{r.residual_risk}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card tid="er-architecture">
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Network className="w-4 h-4 text-cobalt" /> Security architecture</h3>
            <div className="mt-3 space-y-1.5">
              {d.architecture_diagram.nodes.map((n) => (
                <div key={n.id} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${n.zone === "Vendor" ? "bg-amber-50 text-amber-600" : n.zone === "Private" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{n.zone}</span>
                  <span className="text-slate-600">{n.label}</span>
                </div>
              ))}
            </div>
            <h4 className="font-heading font-semibold text-navy text-xs mt-3">Data flow paths</h4>
            <div className="mt-1.5 space-y-1">
              {d.architecture_diagram.edges.map((e, i) => <div key={i} className="text-[11px] text-slate-500 rounded-lg bg-slate-50 px-3 py-1.5">{e[0]} → {e[1]} <span className="text-slate-300">({e[2]})</span></div>)}
            </div>
          </Card>
          <Card tid="er-dataflows">
            <h3 className="font-heading font-bold text-navy text-sm">External data flows (from live inventory)</h3>
            <div className="mt-3 space-y-1.5 max-h-80 overflow-y-auto">
              {d.data_flows.map((f, i) => (
                <div key={i} className="rounded-xl bg-slate-50 px-3 py-2 text-xs">
                  <b className="text-navy">{f.name}</b>
                  <div className="text-slate-400 text-[10px] mt-0.5">{f.classification} · leaves platform: {String(f.flow?.leaves_platform ?? false)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card tid="er-questionnaire">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileQuestion className="w-4 h-4 text-cobalt" /> Due-diligence questionnaire auto-fill</h3>
            <button onClick={loadQa} data-testid="er-qa-toggle" className="px-4 py-2 rounded-full bg-navy text-white text-xs font-bold">{showQa ? "Hide answers" : "Generate answers from live data"}</button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Answers customer security questionnaires, vendor questionnaires and RFP security sections directly from live control state.</p>
          {showQa && qa && (
            <div className="mt-3 space-y-1.5 max-h-96 overflow-y-auto" data-testid="er-qa-list">
              {qa.answers.map((a, i) => (
                <div key={i} className="rounded-xl border border-slate-100 px-3 py-2 text-xs">
                  <b className="text-navy">{a.question}</b>
                  <div className="text-slate-500 mt-0.5">{a.answer} <span className="text-slate-300">· {a.control_ref} · {a.evidence_count} evidence · confidence: {a.confidence}</span></div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card tid="er-crm-integrations">
          <h3 className="font-heading font-bold text-navy text-sm mb-3">Enterprise CRM Integrations</h3>
          <EnterpriseCrmSection compact />
          <div className="mt-3"><EnterpriseValuePanel compact /></div>
        </Card>
        <p className="text-[10px] text-slate-400">{d.note}</p>
      </div>
    </div>
  );
}
